// Dedicated translations for the patient intake questionnaire.
// Kept separate from the marketing-site translations to avoid bloating those
// bundles with medical vocabulary that public pages never use.

export type IntakeLanguage = 'de' | 'en' | 'es' | 'ru';

export interface IntakeStrings {
    meta: {
        pageTitle: string;
        loading: string;
        error_link: string;
        error_generic: string;
        already_submitted_title: string;
        already_submitted_body: string;
        success_title: string;
        success_body: string;
    };
    intro: {
        title: string;
        motto: string;
        greeting: string;
        subtitle: string;
        confidentiality: string;
    };
    validation: {
        toast_incomplete: string;
    };
    nav: {
        back: string;
        next: string;
        submit: string;
        submitting: string;
        step_of: string; // e.g. "Schritt {{current}} von {{total}}"
    };
    steps: {
        personal: string;
        health: string;
        infectious: string;
        medications: string;
        special_meds: string;
        consent: string;
        marketing: string;
    };
    personal: {
        section_title: string;
        name: string;
        street: string;
        postal_code: string;
        city: string;
        country: string;
        country_placeholder: string;
        email: string;
        date_of_birth: string;
        place_of_birth: string;
        phone_private: string;
        phone_work: string;
        occupation: string;
        nie: string;
        doctor_name: string;
        doctor_address: string;
        doctor_phone: string;
    };
    health: {
        section_title: string;
        section_subtitle: string;
        group_cardiovascular: string;
        group_general: string;
        flags: Record<string, string>;
        other_diseases_text_label: string;
    };
    infectious: {
        section_title: string;
        section_subtitle: string;
        group_infectious: string;
        group_allergies: string;
        flags: Record<string, string>;
        other_infectious_text_label: string;
        metals_allergy_label: string;
    };
    medications: {
        section_title: string;
        pregnancy_q: string;
        pregnancy_month: string;
        xray_q: string;
        xray_when: string;
        meds_q: string;
        meds_name: string;
        meds_since: string;
        meds_add: string;
        meds_remove: string;
        meds_empty_hint: string;
    };
    special: {
        section_title: string;
        flags: Record<string, string>;
        since: string;
        major_surgery_q: string;
        major_surgery_date: string;
    };
    consent: {
        section_title: string;
        block_text: string;
        accept_checkbox: string;
        place_label: string;
        place_placeholder: string;
        date_label: string;
        signature_label: string;
        signature_hint: string;
        signature_clear: string;
    };
    marketing: {
        section_title: string;
        intro_paragraph: string;       // Datenverantwortlicher + Adresse + NIF
        purpose_heading: string;
        purpose_text: string;
        legal_basis: string;
        retention: string;
        recipients: string;
        rights_heading: string;
        rights_text: string;
        email_label: string;
        email_description: string;
        whatsapp_label: string;
        whatsapp_description: string;
        phone_label: string;
        phone_description: string;
        opt_out_note: string;
    };
    success: {
        title: string;
        body: string;
    };
    common: {
        yes: string;
        no: string;
        unsure: string;
        required: string;
    };
}

// ---------- DEUTSCH (Master, alle Felder vollständig) ----------
const de: IntakeStrings = {
    meta: {
        pageTitle: 'Patientenerhebungsbogen — SeaDent Mallorca',
        loading: 'Lädt …',
        error_link: 'Dieser Link ist ungültig oder abgelaufen. Bitte wenden Sie sich an die Praxis.',
        error_generic: 'Es ist ein Fehler aufgetreten. Bitte versuchen Sie es erneut.',
        already_submitted_title: 'Bogen bereits ausgefüllt',
        already_submitted_body: 'Vielen Dank — Ihr Erhebungsbogen liegt uns vor. Bei Änderungen melden Sie sich gerne in der Praxis.',
        success_title: 'Vielen Dank!',
        success_body: 'Ihr Erhebungsbogen wurde erfolgreich übermittelt. Wir freuen uns auf Ihren Besuch in der Praxis.',
    },
    intro: {
        title: 'Patientenerhebungsbogen',
        motto: 'Your dental practice for a better life',
        greeting: 'Schön, dass Sie sich für SeaDent entschieden haben. Wir freuen uns auf Sie!',
        subtitle: 'Damit wir Sie bestmöglich behandeln können, bitten wir Sie, einige Fragen zu Ihrer Gesundheit zu beantworten. Nehmen Sie sich ruhig die Zeit — bei Unsicherheiten klären wir alles gemeinsam beim Termin.',
        confidentiality: 'Ihre Angaben unterliegen der ärztlichen Schweigepflicht und werden streng vertraulich behandelt.',
    },
    nav: {
        back: 'Zurück',
        next: 'Weiter',
        submit: 'Bogen abschicken',
        submitting: 'Wird übermittelt …',
        step_of: 'Schritt {{current}} von {{total}}',
    },
    steps: {
        personal: 'Ihre Daten',
        health: 'Gesundheit',
        infectious: 'Infektionen & Allergien',
        medications: 'Medikamente',
        special_meds: 'Spezielle Therapien',
        consent: 'Einwilligung',
        marketing: 'Marketing',
    },
    personal: {
        section_title: 'Ihre Kontaktdaten',
        name: 'Vor- und Nachname',
        street: 'Straße und Hausnummer',
        postal_code: 'PLZ',
        city: 'Ort',
        country: 'Land',
        country_placeholder: 'Land auswählen',
        email: 'E-Mail',
        date_of_birth: 'Geburtsdatum',
        place_of_birth: 'Geburtsort',
        phone_private: 'Telefon (privat)',
        phone_work: 'Telefon (dienstlich)',
        occupation: 'Beruf',
        nie: 'N.I.E. / Personalausweis',
        doctor_name: 'Hausarzt — Name',
        doctor_address: 'Hausarzt — Anschrift',
        doctor_phone: 'Hausarzt — Telefon',
    },
    health: {
        section_title: 'Allgemeine Gesundheit',
        section_subtitle: 'Tippen Sie für jede Erkrankung „Ja" oder „Nein". Wenn Sie unsicher sind, fragen wir später nach.',
        group_cardiovascular: 'Herz und Kreislauf',
        group_general: 'Weitere Erkrankungen',
        flags: {
            high_blood_pressure: 'Hoher Blutdruck',
            low_blood_pressure: 'Niedriger Blutdruck',
            heart_valve_disease: 'Herzklappenfehler',
            heart_valve_replacement: 'Herzklappenersatz',
            pacemaker: 'Herzschrittmacher',
            endocarditis: 'Endokarditis',
            heart_surgery: 'Herzoperation',
            epilepsy: 'Anfallsleiden (Epilepsie)',
            asthma: 'Asthma / Lungenerkrankung',
            blood_clotting: 'Blutgerinnungsstörungen',
            diabetes: 'Diabetes / Zuckerkrankheit',
            drug_dependency: 'Drogenabhängigkeit',
            nerve_disease: 'Nervenerkrankung',
            kidney_disease: 'Nierenerkrankung',
            fainting_spells: 'Ohnmachtsanfälle',
            osteoporosis: 'Osteoporose',
            severe_neutropenia: 'Hochgradige Neutropenie',
            cystic_fibrosis: 'Mukoviszidose',
            organ_transplant: 'Organtransplantation',
            stem_cell_transplant: 'Stammzelltransplantation',
            smoker: 'Raucher',
            rheumatism: 'Rheuma / Arthritis',
            thyroid_disease: 'Schilddrüsenerkrankung',
            other_diseases: 'Sonstige Erkrankung',
        },
        other_diseases_text_label: 'Welche sonstige Erkrankung?',
    },
    infectious: {
        section_title: 'Infektionen und Allergien',
        section_subtitle: 'Diese Angaben sind wichtig für eine sichere Behandlung.',
        group_infectious: 'Infektiöse Erkrankungen',
        group_allergies: 'Allergien und Unverträglichkeiten',
        flags: {
            hiv: 'HIV / Stadium AIDS',
            hepatitis: 'Lebererkrankung / Hepatitis',
            tuberculosis: 'Tuberkulose',
            other_infectious: 'Andere Infektionskrankheit (z. B. MRSA)',
            local_anesthesia: 'Lokalanästhesie / Spritzen',
            antibiotics: 'Antibiotika',
            pain_medication: 'Schmerzmittel',
        },
        other_infectious_text_label: 'Welche Infektionskrankheit?',
        metals_allergy_label: 'Allergie gegen Metalle (welche?)',
    },
    medications: {
        section_title: 'Medikamente und Zustände',
        pregnancy_q: 'Besteht eine Schwangerschaft?',
        pregnancy_month: 'In welchem Monat?',
        xray_q: 'Sind bei Ihnen bereits zahnärztliche Röntgenaufnahmen gemacht worden?',
        xray_when: 'Wenn ja, wann?',
        meds_q: 'Welche Medikamente nehmen Sie regelmäßig oder zurzeit ein?',
        meds_name: 'Medikament',
        meds_since: 'Seit wann',
        meds_add: 'Medikament hinzufügen',
        meds_remove: 'Entfernen',
        meds_empty_hint: 'Keine Medikamente — Sie können den Schritt überspringen.',
    },
    special: {
        section_title: 'Spezielle Therapien und Operationen',
        flags: {
            bisphosphonates: 'Nehmen Sie Bisphosphonate?',
            chemotherapy: 'Stehen Sie in medikamentöser Chemotherapie?',
            radiation_therapy: 'Stehen Sie in Strahlentherapie bei Krebserkrankung?',
            steroids: 'Nehmen Sie hochdosierte Steroide oder Immunsuppressiva?',
        },
        since: 'Seit wann?',
        major_surgery_q: 'Wurden bei Ihnen größere Operationen im Krankenhaus durchgeführt?',
        major_surgery_date: 'Datum der Operation',
    },
    consent: {
        section_title: 'Einwilligung und Unterschrift',
        block_text: 'Ich erkläre mich mit der elektronischen Speicherung, Bearbeitung und Nutzung meiner Daten zur Aufnahme in das System einverstanden. Ich verpflichte mich, über alle Änderungen, die sich während der gesamten Behandlungszeit ergeben, umgehend Mitteilung zu machen. Des Weiteren verpflichte ich mich, vereinbarte Behandlungstermine einzuhalten bzw. mindestens 24 Std. vor dem vereinbarten Termin abzusagen. Mir ist bekannt, dass nicht oder nicht rechtzeitig abgesagte Termine in Rechnung gestellt werden können. Ich bin damit einverstanden, dass bei umfangreichen zahnärztlichen oder zahntechnischen Leistungen, für die Sea-Dent gegenüber dem Zahntechniker in finanzielle Vorleistung treten muss, gegebenenfalls eine Bonitätsanfrage bei einem Kreditschutzunternehmen oder einer Auskunftei eingeholt wird. Ich wurde über die Behandlungskosten ausführlich informiert und verpflichte mich hiermit, diese zu übernehmen.',
        accept_checkbox: 'Ich habe den Text gelesen und stimme zu.',
        place_label: 'Ort',
        place_placeholder: 'z. B. Palma de Mallorca',
        date_label: 'Datum',
        signature_label: 'Unterschrift',
        signature_hint: 'Bitte unterschreiben Sie mit dem Finger oder der Maus.',
        signature_clear: 'Zurücksetzen',
    },
    marketing: {
        section_title: 'Einwilligung zur Marketing-Kommunikation',
        intro_paragraph: 'Verantwortlich für die Datenverarbeitung: SeaDent Mallorca, Avenida de Jaime III, Nr. 3, 07012 Palma de Mallorca (Illes Balears, Spanien), NIF: [NIF einsetzen], E-Mail: info@seadent.es. Diese Einwilligung ist freiwillig und unabhängig von Ihrer zahnärztlichen Behandlung.',
        purpose_heading: 'Zweck der Verarbeitung',
        purpose_text: 'Versand von Informationen zu Praxisangeboten, Aktionen, Patienten-Newslettern und Praxisneuigkeiten über die unten ausgewählten Kanäle.',
        legal_basis: 'Rechtsgrundlage: Ihre ausdrückliche Einwilligung gemäß Art. 6 Abs. 1 lit. a DSGVO (RGPD) sowie Art. 21 LSSI-CE für elektronische Kommunikation.',
        retention: 'Speicherdauer: bis zu Ihrem Widerruf. Nach Widerruf werden Ihre Kontaktdaten unverzüglich aus den Marketing-Verteilern entfernt.',
        recipients: 'Empfänger: technische Dienstleister (E-Mail-, Messaging- und Telefonie-Provider), die vertraglich auf den Schutz Ihrer Daten verpflichtet sind. Keine Weitergabe an Dritte zu deren eigenen Werbezwecken.',
        rights_heading: 'Ihre Rechte',
        rights_text: 'Sie haben das Recht auf Auskunft, Berichtigung, Löschung, Einschränkung der Verarbeitung, Datenübertragbarkeit und Widerspruch. Sie können Ihre Einwilligung jederzeit ohne Angabe von Gründen per E-Mail an info@seadent.es widerrufen — die Rechtmäßigkeit der bis dahin erfolgten Verarbeitung bleibt unberührt, und Ihre Behandlung wird davon in keiner Weise beeinflusst. Beschwerderecht bei der Aufsichtsbehörde: Agencia Española de Protección de Datos (AEPD, www.aepd.es).',
        email_label: 'Ja, ich möchte Informationen per E-Mail erhalten.',
        email_description: 'Newsletter, Angebote und Praxisneuigkeiten an Ihre oben angegebene E-Mail-Adresse.',
        whatsapp_label: 'Ja, ich möchte Informationen per WhatsApp erhalten.',
        whatsapp_description: 'Aktionen, Erinnerungen und Angebote per WhatsApp an die oben angegebene Telefonnummer.',
        phone_label: 'Ja, ich bin mit Marketing-Anrufen einverstanden.',
        phone_description: 'Telefonische Information zu neuen Behandlungen, Aktionen und Praxisangeboten.',
        opt_out_note: 'Sie können diese Einwilligungen jederzeit per E-Mail an info@seadent.es widerrufen, ohne dass dies Auswirkungen auf Ihre zahnärztliche Behandlung hat.',
    },
    success: {
        title: 'Vielen Dank!',
        body: 'Ihr Erhebungsbogen ist bei uns eingegangen. Wir freuen uns auf Ihren Termin.',
    },
    common: {
        yes: 'Ja',
        no: 'Nein',
        unsure: 'Unsicher',
        required: 'Pflichtfeld',
    },
    validation: {
        toast_incomplete: 'Bitte beantworten Sie freundlicherweise alle Fragen, damit wir Sie sicher behandeln können.',
    },
};

// ---------- ENGLISH ----------
const en: IntakeStrings = {
    meta: {
        pageTitle: 'Patient Questionnaire — SeaDent Mallorca',
        loading: 'Loading …',
        error_link: 'This link is invalid or expired. Please contact the practice.',
        error_generic: 'Something went wrong. Please try again.',
        already_submitted_title: 'Questionnaire already submitted',
        already_submitted_body: 'Thank you — we already have your questionnaire on file. For changes please contact the practice.',
        success_title: 'Thank you!',
        success_body: 'Your questionnaire has been submitted successfully. We look forward to welcoming you at the practice.',
    },
    intro: {
        title: 'Patient Questionnaire',
        motto: 'Your dental practice for a better life',
        greeting: "We're delighted you've chosen SeaDent. Welcome!",
        subtitle: 'Please take a moment to answer a few questions about your health so we can treat you safely. If anything is unclear, we will go through it together at your appointment.',
        confidentiality: 'Your answers are subject to medical confidentiality and treated strictly private.',
    },
    nav: {
        back: 'Back',
        next: 'Next',
        submit: 'Submit questionnaire',
        submitting: 'Submitting …',
        step_of: 'Step {{current}} of {{total}}',
    },
    steps: {
        personal: 'Your details',
        health: 'Health',
        infectious: 'Infections & allergies',
        medications: 'Medications',
        special_meds: 'Special therapies',
        consent: 'Consent',
        marketing: 'Marketing',
    },
    personal: {
        section_title: 'Your contact details',
        name: 'First and last name',
        street: 'Street and house number',
        postal_code: 'Postal code',
        city: 'City',
        country: 'Country',
        country_placeholder: 'Select country',
        email: 'Email',
        date_of_birth: 'Date of birth',
        place_of_birth: 'Place of birth',
        phone_private: 'Phone (home)',
        phone_work: 'Phone (work)',
        occupation: 'Occupation',
        nie: 'N.I.E. / ID number',
        doctor_name: 'Primary physician — name',
        doctor_address: 'Primary physician — address',
        doctor_phone: 'Primary physician — phone',
    },
    health: {
        section_title: 'General health',
        section_subtitle: 'Tap "Yes" or "No" for each condition. If you are unsure, we will ask later.',
        group_cardiovascular: 'Heart & cardiovascular',
        group_general: 'Other conditions',
        flags: {
            high_blood_pressure: 'High blood pressure',
            low_blood_pressure: 'Low blood pressure',
            heart_valve_disease: 'Heart valve disease',
            heart_valve_replacement: 'Heart valve replacement',
            pacemaker: 'Pacemaker',
            endocarditis: 'Endocarditis',
            heart_surgery: 'Heart surgery',
            epilepsy: 'Epilepsy',
            asthma: 'Asthma / lung disease',
            blood_clotting: 'Blood clotting disorders',
            diabetes: 'Diabetes',
            drug_dependency: 'Drug dependency',
            nerve_disease: 'Nerve disease',
            kidney_disease: 'Kidney disease',
            fainting_spells: 'Fainting spells',
            osteoporosis: 'Osteoporosis',
            severe_neutropenia: 'Severe neutropenia',
            cystic_fibrosis: 'Cystic fibrosis',
            organ_transplant: 'Organ transplant',
            stem_cell_transplant: 'Stem cell transplant',
            smoker: 'Smoker',
            rheumatism: 'Rheumatism / arthritis',
            thyroid_disease: 'Thyroid disease',
            other_diseases: 'Other condition',
        },
        other_diseases_text_label: 'Which other condition?',
    },
    infectious: {
        section_title: 'Infectious diseases & allergies',
        section_subtitle: 'This information is important for safe treatment.',
        group_infectious: 'Infectious diseases',
        group_allergies: 'Allergies & intolerances',
        flags: {
            hiv: 'HIV / AIDS',
            hepatitis: 'Liver disease / hepatitis',
            tuberculosis: 'Tuberculosis',
            other_infectious: 'Other infectious disease (e.g. MRSA)',
            local_anesthesia: 'Local anesthesia / injections',
            antibiotics: 'Antibiotics',
            pain_medication: 'Pain medication',
        },
        other_infectious_text_label: 'Which infectious disease?',
        metals_allergy_label: 'Metal allergy (which?)',
    },
    medications: {
        section_title: 'Medications & conditions',
        pregnancy_q: 'Are you pregnant?',
        pregnancy_month: 'Which month?',
        xray_q: 'Have you had dental X-rays before?',
        xray_when: 'If yes, when?',
        meds_q: 'Which medications do you take regularly or currently?',
        meds_name: 'Medication',
        meds_since: 'Since when',
        meds_add: 'Add medication',
        meds_remove: 'Remove',
        meds_empty_hint: 'No medications — you can skip this step.',
    },
    special: {
        section_title: 'Special therapies & surgeries',
        flags: {
            bisphosphonates: 'Are you taking bisphosphonates?',
            chemotherapy: 'Are you receiving chemotherapy?',
            radiation_therapy: 'Are you receiving radiation therapy for cancer?',
            steroids: 'Are you taking high-dose steroids or immunosuppressants?',
        },
        since: 'Since when?',
        major_surgery_q: 'Have you had major surgery in hospital?',
        major_surgery_date: 'Date of surgery',
    },
    consent: {
        section_title: 'Consent & signature',
        block_text: 'I consent to the electronic storage, processing, and use of my data for input into the practice management system. I agree to immediately report any changes during the entire treatment period. I further agree to keep all scheduled treatment appointments or to cancel them at least 24 hours in advance, and understand that appointments not cancelled in time may be billed. I understand that for extensive dental work involving advance payment to the dental technician, a credit check may be carried out. I have been informed about treatment costs and agree to pay them.',
        accept_checkbox: 'I have read the text and agree.',
        place_label: 'Place',
        place_placeholder: 'e.g. Palma de Mallorca',
        date_label: 'Date',
        signature_label: 'Signature',
        signature_hint: 'Please sign with your finger or mouse.',
        signature_clear: 'Clear',
    },
    marketing: {
        section_title: 'Marketing communications consent',
        intro_paragraph: 'Data controller: SeaDent Mallorca, Avenida de Jaime III, No. 3, 07012 Palma de Mallorca (Balearic Islands, Spain), NIF: [insert NIF], email: info@seadent.es. This consent is voluntary and independent of your dental treatment.',
        purpose_heading: 'Purpose of processing',
        purpose_text: 'Sending information about practice offers, promotions, patient newsletters, and practice news through the channels you select below.',
        legal_basis: 'Legal basis: your explicit consent under Art. 6(1)(a) GDPR (RGPD) and Art. 21 LSSI-CE for electronic communications.',
        retention: 'Storage period: until you withdraw consent. After withdrawal your contact details are removed from marketing lists without delay.',
        recipients: 'Recipients: technical service providers (email, messaging, and telephony providers) bound by contract to protect your data. No transfer to third parties for their own advertising purposes.',
        rights_heading: 'Your rights',
        rights_text: 'You have the right to access, rectification, erasure, restriction of processing, data portability, and objection. You may withdraw your consent at any time without giving a reason by emailing info@seadent.es — the lawfulness of processing carried out before withdrawal remains unaffected, and your treatment will not be impacted in any way. Right to lodge a complaint with the supervisory authority: Agencia Española de Protección de Datos (AEPD, www.aepd.es).',
        email_label: 'Yes, I would like to receive information by email.',
        email_description: 'Newsletter, offers, and practice news to the email address you provided above.',
        whatsapp_label: 'Yes, I would like to receive information via WhatsApp.',
        whatsapp_description: 'Promotions, reminders, and offers via WhatsApp to the phone number you provided above.',
        phone_label: 'Yes, I consent to marketing phone calls.',
        phone_description: 'Phone information about new treatments, promotions, and practice offers.',
        opt_out_note: 'You may withdraw these consents at any time by emailing info@seadent.es, with no impact on your dental treatment.',
    },
    success: {
        title: 'Thank you!',
        body: 'We have received your questionnaire. We look forward to your appointment.',
    },
    common: {
        yes: 'Yes',
        no: 'No',
        unsure: 'Unsure',
        required: 'Required',
    },
    validation: {
        toast_incomplete: 'Please kindly answer all questions so we can treat you safely.',
    },
};

// ---------- ESPAÑOL ----------
const es: IntakeStrings = {
    meta: {
        pageTitle: 'Formulario para pacientes — SeaDent Mallorca',
        loading: 'Cargando …',
        error_link: 'Este enlace no es válido o ha caducado. Por favor, póngase en contacto con la clínica.',
        error_generic: 'Ha ocurrido un error. Por favor, inténtelo de nuevo.',
        already_submitted_title: 'Formulario ya enviado',
        already_submitted_body: 'Muchas gracias — ya tenemos su formulario. Para cualquier modificación, póngase en contacto con la clínica.',
        success_title: '¡Muchas gracias!',
        success_body: 'Su cuestionario se ha enviado correctamente. Estaremos encantados de recibirle en la clínica.',
    },
    intro: {
        title: 'Formulario para pacientes',
        motto: 'Your dental practice for a better life',
        greeting: '¡Nos alegra que haya elegido SeaDent. Bienvenido!',
        subtitle: 'Tómese un momento para responder a algunas preguntas sobre su estado de salud, así podremos atenderle de forma segura. Si tiene dudas, las aclararemos juntos en la cita.',
        confidentiality: 'Sus datos están sometidos al secreto médico y serán tratados de forma estrictamente confidencial.',
    },
    nav: {
        back: 'Atrás',
        next: 'Siguiente',
        submit: 'Enviar formulario',
        submitting: 'Enviando …',
        step_of: 'Paso {{current}} de {{total}}',
    },
    steps: {
        personal: 'Sus datos',
        health: 'Salud',
        infectious: 'Infecciones y alergias',
        medications: 'Medicación',
        special_meds: 'Terapias especiales',
        consent: 'Consentimiento',
        marketing: 'Marketing',
    },
    personal: {
        section_title: 'Sus datos de contacto',
        name: 'Apellidos y nombre',
        street: 'Calle y número',
        postal_code: 'Código postal',
        city: 'Localidad',
        country: 'País',
        country_placeholder: 'Seleccionar país',
        email: 'Correo electrónico',
        date_of_birth: 'Fecha de nacimiento',
        place_of_birth: 'Lugar de nacimiento',
        phone_private: 'Teléfono (privado)',
        phone_work: 'Teléfono (trabajo)',
        occupation: 'Profesión',
        nie: 'N.I.E. / DNI',
        doctor_name: 'Médico de cabecera — nombre',
        doctor_address: 'Médico de cabecera — dirección',
        doctor_phone: 'Médico de cabecera — teléfono',
    },
    health: {
        section_title: 'Salud general',
        section_subtitle: 'Pulse «Sí» o «No» para cada afección. Si tiene dudas, podremos aclararlo después.',
        group_cardiovascular: 'Corazón y circulación',
        group_general: 'Otras afecciones',
        flags: {
            high_blood_pressure: 'Alta tensión',
            low_blood_pressure: 'Baja tensión',
            heart_valve_disease: 'Defecto valvular',
            heart_valve_replacement: 'Válvula cardiaca artificial',
            pacemaker: 'Marcapasos',
            endocarditis: 'Endocarditis',
            heart_surgery: 'Operación del corazón',
            epilepsy: 'Ataques (epilepsia)',
            asthma: 'Asma / Enfermedades pulmonares',
            blood_clotting: 'Defectos de la coagulación sanguínea',
            diabetes: 'Diabetes',
            drug_dependency: 'Drogadicción',
            nerve_disease: 'Enfermedad nerviosa',
            kidney_disease: 'Enfermedad de los riñones',
            fainting_spells: 'Desmayos',
            osteoporosis: 'Osteoporosis',
            severe_neutropenia: 'Neutropenia de alto grado',
            cystic_fibrosis: 'Mucoviscidosis',
            organ_transplant: 'Trasplante de órganos',
            stem_cell_transplant: 'Trasplante de células germinales',
            smoker: 'Fumador',
            rheumatism: 'Reuma / Artritis',
            thyroid_disease: 'Enfermedad de la glándula tiroides',
            other_diseases: 'Otra enfermedad',
        },
        other_diseases_text_label: '¿Qué otra enfermedad?',
    },
    infectious: {
        section_title: 'Enfermedades infecciosas y alergias',
        section_subtitle: 'Esta información es importante para un tratamiento seguro.',
        group_infectious: 'Enfermedades infecciosas',
        group_allergies: 'Alergias e intolerancias',
        flags: {
            hiv: 'Infección por VIH / Estado SIDA',
            hepatitis: 'Enfermedad de hígado / Hepatitis',
            tuberculosis: 'Tuberculosis',
            other_infectious: 'Otra enfermedad infecciosa (p. ej. MRSA)',
            local_anesthesia: 'Anestesia local / Inyecciones',
            antibiotics: 'Antibióticos',
            pain_medication: 'Analgésicos',
        },
        other_infectious_text_label: '¿Qué enfermedad infecciosa?',
        metals_allergy_label: 'Alergia a metales (¿cuáles?)',
    },
    medications: {
        section_title: 'Medicación y estados',
        pregnancy_q: '¿Está embarazada?',
        pregnancy_month: '¿De qué mes?',
        xray_q: '¿Se le han hecho ya radiografías dentales?',
        xray_when: 'Si es así, ¿cuándo?',
        meds_q: '¿Qué medicinas toma con regularidad o ahora mismo?',
        meds_name: 'Medicamento',
        meds_since: 'Desde',
        meds_add: 'Añadir medicamento',
        meds_remove: 'Eliminar',
        meds_empty_hint: 'Sin medicación — puede omitir este paso.',
    },
    special: {
        section_title: 'Terapias especiales y cirugías',
        flags: {
            bisphosphonates: '¿Toma bisfosfonatos?',
            chemotherapy: '¿Realiza una quimioterapia medicamentosa?',
            radiation_therapy: '¿Realiza una radioterapia porque padece de cáncer?',
            steroids: '¿Toma esteroides de alta dosificación o inmunosupresores?',
        },
        since: '¿Desde cuándo?',
        major_surgery_q: '¿Se le han hecho operaciones mayores en el hospital?',
        major_surgery_date: 'Fecha de la operación',
    },
    consent: {
        section_title: 'Consentimiento y firma',
        block_text: 'Declaro estar de acuerdo con que se almacenen, procesen y aprovechen mis datos para ser incluidos en el sistema. Me obligo a comunicar de inmediato todos los cambios que tengan lugar durante todo el período terapéutico. Me obligo igualmente a observar las fechas del tratamiento acordadas o bien a anular la fecha acordada como mínimo 24 horas antes. Sé que si no anulo o bien no anuló a tiempo las fechas acordadas, se me podrán poner en factura. Estoy de acuerdo que para las prestaciones odontológicas o protésicas que Sea-Dent tenga que abonar por anticipado al técnico dental, se consulte a una institución protectora de créditos o a una agencia de informes sobre la solvencia, en caso de que fuere necesario. Habiendo sido informado de los costes del tratamiento, me comprometo a abonarlos.',
        accept_checkbox: 'He leído el texto y estoy de acuerdo.',
        place_label: 'Lugar',
        place_placeholder: 'p. ej. Palma de Mallorca',
        date_label: 'Fecha',
        signature_label: 'Firma',
        signature_hint: 'Firme con el dedo o con el ratón.',
        signature_clear: 'Borrar',
    },
    marketing: {
        section_title: 'Consentimiento para comunicaciones de marketing',
        intro_paragraph: 'Responsable del tratamiento: SeaDent Mallorca, Avenida de Jaime III, n.º 3, 07012 Palma de Mallorca (Illes Balears, España), NIF: [insertar NIF], correo electrónico: info@seadent.es. Este consentimiento es voluntario e independiente de su tratamiento odontológico.',
        purpose_heading: 'Finalidad del tratamiento',
        purpose_text: 'Envío de información sobre ofertas, promociones, boletines para pacientes y novedades de la clínica a través de los canales que usted seleccione a continuación.',
        legal_basis: 'Base jurídica: su consentimiento expreso conforme al art. 6.1.a) del RGPD y al art. 21 de la LSSI-CE para comunicaciones electrónicas.',
        retention: 'Plazo de conservación: hasta la retirada de su consentimiento. Una vez retirado, sus datos de contacto se eliminarán sin demora de las listas de marketing.',
        recipients: 'Destinatarios: prestadores de servicios técnicos (proveedores de correo electrónico, mensajería y telefonía) obligados contractualmente a proteger sus datos. Sin cesión a terceros para finalidades publicitarias propias de éstos.',
        rights_heading: 'Sus derechos',
        rights_text: 'Tiene derecho a acceder, rectificar, suprimir, limitar el tratamiento, a la portabilidad y a oponerse. Puede retirar su consentimiento en cualquier momento sin necesidad de justificación enviando un correo electrónico a info@seadent.es — la licitud del tratamiento realizado hasta ese momento no se verá afectada, y su tratamiento odontológico no se verá afectado en modo alguno. Derecho a presentar una reclamación ante la autoridad de control: Agencia Española de Protección de Datos (AEPD, www.aepd.es).',
        email_label: 'Sí, deseo recibir información por correo electrónico.',
        email_description: 'Boletines, ofertas y novedades de la clínica en la dirección de correo electrónico indicada arriba.',
        whatsapp_label: 'Sí, deseo recibir información por WhatsApp.',
        whatsapp_description: 'Promociones, recordatorios y ofertas por WhatsApp al número de teléfono indicado arriba.',
        phone_label: 'Sí, consiento llamadas telefónicas con fines de marketing.',
        phone_description: 'Información telefónica sobre nuevos tratamientos, promociones y ofertas de la clínica.',
        opt_out_note: 'Puede retirar estos consentimientos en cualquier momento enviando un correo electrónico a info@seadent.es, sin que ello afecte a su tratamiento odontológico.',
    },
    success: {
        title: '¡Muchas gracias!',
        body: 'Hemos recibido su formulario. Le esperamos en su cita.',
    },
    common: {
        yes: 'Sí',
        no: 'No',
        unsure: 'No estoy seguro',
        required: 'Obligatorio',
    },
    validation: {
        toast_incomplete: 'Por favor, responda amablemente a todas las preguntas para que podamos atenderle de forma segura.',
    },
};

// ---------- РУССКИЙ — fallback to English until full translation lands ----------
const ru: IntakeStrings = en;

const TABLE: Record<IntakeLanguage, IntakeStrings> = { de, en, es, ru };

// Languages we have full translations for. RU has no medical-domain translation
// yet — the form falls back to one of these instead of rendering English strings
// in a Russian-tagged shell (which mismatches hyphenation, Intl.DisplayNames, etc).
const SUPPORTED_LANGS: IntakeLanguage[] = ['de', 'en', 'es'];

/**
 * Resolves the appointment language to an actually-translated form language.
 * Used everywhere strings/locale-sensitive APIs are needed (Intl.DisplayNames,
 * html lang attribute, hyphens-auto). RU → EN.
 */
export function resolveFormLanguage(language: string | undefined): IntakeLanguage {
    const lang = (language || 'de') as IntakeLanguage;
    return SUPPORTED_LANGS.includes(lang) ? lang : 'en';
}

export function getIntakeStrings(language: string | undefined): IntakeStrings {
    return TABLE[resolveFormLanguage(language)] || de;
}
